import Link from 'next/link';
import VisibilitySensor from 'react-visibility-sensor';
import styled, { css } from 'styled-components';
import isString from 'lodash/isString';
import { useState, useEffect } from 'react';

import bounceInAnimation from 'styles/theme/animation';
import { Caret } from 'shared/icons/Caret';
import { Image } from 'shared/components/Image';
import { breakpoints } from 'styles/theme/breakpoints';
import { useOutsideClick } from 'shared/hooks/useOutsideClick';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';

function convertToPercent(val) {
  return `${val * 100}%`;
}

// min = 75, max = 200
// 11 = 74px
// 17 = 135px

function getLabelWidth(name) {
  if (!isString(name)) return 250;
  const len = name.length;
  if (len < 12) return 80;
  if (len < 14) return 100;
  if (len < 18) return 140;
  if (len < 23) return 180;
  if (len < 27) return 210;
  return 250;
}

export const ArticleProductMarker = (props) => {
  const { marker, openInNewWindow = false, revealMarker } = props;
  const [isLabelExpanded, setIsLabelExpanded] = useState(false);
  const [isMarkerRevealed, setIsMarkerRevealed] = useState(revealMarker);
  const { x, y, productTemplate } = marker;
  const hideLabel = () => {
    setIsLabelExpanded(false);
  };
  const handleClickOutside = () => {
    hideLabel();
  };
  const hotspotRef = useOutsideClick(handleClickOutside);

  useEffect(() => {
    if (isLabelExpanded) {
      window.addEventListener('scroll', hideLabel);
    } else {
      window.removeEventListener('scroll', hideLabel);
    }

    return () => {
      window.removeEventListener('scroll', hideLabel);
    };
  }, [isLabelExpanded]);

  const onVisibilityChange = (isVisible) => {
    if (isVisible) {
      if (!hotspotRef.current) return;
      if (isMarkerRevealed) return;
      setIsMarkerRevealed(true);
    } else {
      hideLabel();
    }
  };

  // TODO: Refactor this, it's totally unclear what's going on. -DC
  // Position the expanded label in relation to the hotspotRef trigger
  const getLabelOffset = () => {
    if (!hotspotRef.current) return '50%';
    const xPos = hotspotRef.current.getBoundingClientRect().x;
    const vWidth = window.innerWidth;
    if (xPos < 75) return 130;
    if (xPos < 150) return 100;
    if (xPos > vWidth - 150) return -100;
    if (xPos > vWidth - 75) return -130;
    return '50%';
  };

  const handleHotspotClick = () => {
    if (!isLabelExpanded && productTemplate) {
      setIsLabelExpanded(true);
      sendTrackingEvent('ARTICLE_HOTSPOT_CLICK', {
        product_template_slug: productTemplate?.slug,
      });
    }
  };

  const handleLinkClick = () => {
    if (productTemplate)
      sendTrackingEvent('ARTICLE_HOTSPOT_VIEW', {
        product_template_slug: productTemplate?.slug,
      });
  };

  const markerPosition = {
    bottom: isString(y) ? y : convertToPercent(y),
    left: isString(x) ? x : convertToPercent(x),
  };

  return (
    <VisibilitySensor onChange={onVisibilityChange}>
      <ArticleProductMarker.Wrapper
        ref={hotspotRef}
        isMarkerRevealed={isMarkerRevealed}
        showLabel={isLabelExpanded}
        style={{
          ...markerPosition,
        }}
      >
        <ArticleProductMarker.LabelWrapper onClick={handleHotspotClick}>
          <ArticleProductMarker.Hotspot />
          <ArticleProductMarker.Label
            isLabelExpanded={isLabelExpanded}
            style={{ left: getLabelOffset() }}
          >
            <Link href={`/sneakers/${productTemplate?.slug as string}`} passHref>
              <a
                onClick={handleLinkClick}
                onKeyDown={handleLinkClick}
                target={openInNewWindow ? '_blank' : '_self'}
                data-qa="sneakers_product_link"
                role="button"
                tabIndex={0}
              >
                <ArticleProductMarker.LabelContents>
                  <ArticleProductMarker.Image
                    src={productTemplate?.gridPictureUrl}
                    alt={productTemplate?.name}
                    data-qa="sneaker_product_img"
                    sizes={`(max-width: ${breakpoints.medium}px) 90vw, 60vw`}
                  />
                  <ArticleProductMarker.MarkerName
                    style={{ width: getLabelWidth(productTemplate?.name) }}
                  >
                    {productTemplate?.name}
                  </ArticleProductMarker.MarkerName>
                  <Caret size={6} direction="right" />
                </ArticleProductMarker.LabelContents>
              </a>
            </Link>
          </ArticleProductMarker.Label>
        </ArticleProductMarker.LabelWrapper>
      </ArticleProductMarker.Wrapper>
    </VisibilitySensor>
  );
};

ArticleProductMarker.Wrapper = styled.div<{ isMarkerRevealed: boolean; showLabel: boolean }>`
  cursor: pointer;
  position: absolute;
  margin-top: -15px;
  margin-left: -15px;
  z-index: 300;
  opacity: 0;
  transform: scale3d(0.3, 0.3, 0.3);
  ${({ isMarkerRevealed }) =>
    isMarkerRevealed &&
    css`
      ${bounceInAnimation()}
    `};
  ${({ showLabel }) =>
    showLabel &&
    `
      pointer-events: auto;
      opacity: 1;
      transform: translateX(-50%) scale3d(1, 1, 1);
  `}
`;

ArticleProductMarker.Hotspot = styled.div`
  display: flex;
  cursor: pointer;
  height: 30px;
  width: 30px;
  border-radius: 100%;
  background: white url(/images/arrow-line.png) center center no-repeat;
  background-size: 13px auto;
  transition: transform 0.25s ease-in-out;

  &:hover {
    transform: scale3d(1.2, 1.2, 1);
  }
`;

ArticleProductMarker.LabelWrapper = styled.div`
  position: relative;
  width: 100%;
`;

ArticleProductMarker.Label = styled.div<{ isLabelExpanded: boolean }>`
  pointer-events: none;
  position: absolute;
  top: -40%;
  left: 50%;
  opacity: 0;
  transform: translateX(-50%) scale3d(0.2, 0.2, 1);
  transition: all 0.25s ease-out;
  user-select: none;
  svg {
    flex: 0 0 20px;
  }
  ${({ isLabelExpanded }) =>
    isLabelExpanded &&
    `
    pointer-events: auto;
    opacity: 1;
    transform: translateX(-50%) scale3d(1, 1, 1);
  `}
`;

ArticleProductMarker.LabelContents = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  height: 55px;
  padding: 5px 15px;
  min-width: 200px;
  max-width: 300px;
  width: 100%;
  background: white;
  border: 1px solid #000000;
`;

ArticleProductMarker.Image = styled(Image)`
  width: 40px !important;
  height: auto;
  margin: auto 15px auto 0;
`;

ArticleProductMarker.MarkerName = styled.div`
  flex: 1 1 auto;
  font-family: 'GOATProductVariableVF';
  font-weight: 700;
  font-size: 9.5px;
  letter-spacing: 1px;
  text-transform: uppercase;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 2px;
`;
